import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Activity,
    Calendar,
    Clock,
    Download,
    Edit,
    Mail,
    MapPin,
    Plus,
    Shield,
    Target,
    Trash2,
    UserCheck,
    UserPlus,
    Users,
    UserX,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import httpClient from '@/api/httpClient.ts';
import { ConditionalPhoneField } from '@/components/integrations/ConditionalPhoneField';
import { API_ENDPOINTS } from '@/api/endpoints.ts';
import {
    useBulkAssignCustomers,
    useBulkDeleteCustomers,
    useBulkUpdateCustomerStatus,
    useCreateCustomer,
} from '@/api/customers/use-customers.tsx';
import { countries, getCountryByCode } from '@/data/countries';
import { Customer } from '@/models/customer.ts';
import { PaginatedResponse } from '@/models/transaction.ts';
import { usePermissions } from '@/hooks/usePermissions.ts';
import { ModulePermission, PermissionAction } from '@/types/permissions.ts';
import { Card, CardContent } from '@/components/ui/card.tsx';
import DateTimeFormatter from '@/components/shared/DateTimeFormatter';
import { Button } from '@/components/ui/button.tsx';
import { Badge } from '@/components/ui/badge.tsx';
import { GenericDataTable } from '@/components/shared/tables';
import { PageContainer, PageHeader, StatTile, StatTileGrid } from '@/components/shared/layout';
import { EmptyState } from '@/components/shared/EmptyState';
import { Column } from '@/components/ui/data-table';
import { useExportCustomers } from '@/api/export/use-export.tsx';
import { useActiveLeadStatuses } from '@/api/lead-statuses/use-lead-statuses';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog.tsx';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import CustomerForm from '@/components/customers/CustomerForm';
import { useCustomAttributeDefinitionsManagement } from '@/hooks/useCustomAttributes';
import { useUsers } from '@/api/users/use-users';
import { useUrlState } from '@/hooks/shared';
import { useColumnPreferences } from '@/hooks/useColumnPreferences';
import { usePageViews } from '@/hooks/usePageViews';
import { ViewSelector } from '@/components/shared/ViewSelector';
import { useSystemConfiguration } from '@/api/system-configuration/use-system-configuration';
import { buildApiParams } from '@/api/leads/use-leads';

type FilterConfig = Record<string, any>;

const CustomersPage: React.FC = () => {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const { data: systemConfig } = useSystemConfiguration();
    const crmTimezone = systemConfig?.timezone ?? 'UTC';

    // Use URL-based state management
    const {
        filters,
        sort,
        pagination,
        updateFilters,
        clearFilters,
        toggleSort,
        goToPage,
        updatePagination,
        replaceState,
        searchParams,
    } = useUrlState({
        sort: { key: 'created_at', direction: 'desc' },
        pagination: { page: 1, pageSize: 25 },
    });

    const [selectedCustomers, setSelectedCustomers] = useState<string[]>([]);
    const [createDialogOpen, setCreateDialogOpen] = useState(false);

    // Fetch lead statuses for filtering
    const { data: leadStatusesResponse } = useActiveLeadStatuses();
    const leadStatuses = leadStatusesResponse?.data || [];

    // Bulk action dialogs
    const [bulkStatusDialogOpen, setBulkStatusDialogOpen] = useState(false);
    const [bulkAssignDialogOpen, setBulkAssignDialogOpen] = useState(false);
    const [selectedStatusId, setSelectedStatusId] = useState<string>('');
    const [selectedUserId, setSelectedUserId] = useState<string>('');

    // Column visibility state - will be initialized after columns are defined

    // Fetch users for bulk assign
    const { data: usersData } = useUsers({ status: 'active', per_page: 100 });
    const users = usersData?.data || [];

    // Fetch custom attribute definitions for dynamic columns
    const {
        definitions: customAttributeDefinitions,
        isLoading: isLoadingCustomAttributeDefinitions,
    } = useCustomAttributeDefinitionsManagement();

    const handleSort = (key: keyof Customer) => {
        toggleSort(key as string);
    };

    const exportCustomersMutation = useExportCustomers(
        () => {
            enqueueSnackbar('Customers exported successfully', { variant: 'success' });
        },
        (error: Error) => {
            console.error('Export failed:', error);
            enqueueSnackbar('Failed to export customers', { variant: 'error' });
        }
    );

    // Filters + sort of the current view, shared by the list and the export so the
    // export always contains exactly what the list shows (across all pages)
    const viewParams = useMemo(
        () => ({
            search: filters.search,
            sort_by: sort.key,
            sort_direction: sort.direction,
            ...Object.fromEntries(
                Object.entries(filters).filter(
                    ([key, value]) =>
                        key !== 'search' &&
                        value !== undefined &&
                        value !== '' &&
                        value !== null &&
                        value !== 'all'
                )
            ),
        }),
        [filters, sort.key, sort.direction]
    );

    // Exports every customer matching the current view (all pages), or only the given rows
    const handleExportCustomers = (selectedIds?: string[]) => {
        if (exportCustomersMutation.isPending) {
            return;
        }

        const exportFilters = selectedIds?.length
            ? { selected_ids: selectedIds }
            : buildApiParams(viewParams);

        exportCustomersMutation.mutate({
            format: 'csv',
            filters: exportFilters,
        });
    };

    // Define columns for the DataTable - aligned with new Customer model structure
    const columns: Column<Customer>[] = useMemo(
        () => [
            {
                id: 'customer_number',
                label: 'Customer #',
                key: 'customer_number',
                sortable: true,
                width: '120px',
                render: (_, customer) => (
                    <div className="font-mono font-medium text-blue-600">
                        #{customer.customer_number || 'N/A'}
                    </div>
                ),
            },
            {
                id: 'name',
                label: 'Name',
                key: 'name',
                sortable: true,
                width: '200px',
                render: (value, customer) => (
                    <div>
                        <div className="font-medium">{value}</div>
                    </div>
                ),
            },
            {
                id: 'contact',
                label: 'Contact',
                key: 'email',
                sortable: true,
                width: '180px',
                render: (value, customer) => (
                    <div className="space-y-1">
                        <div className="flex items-center gap-1">
                            <Mail className="h-3 w-3 text-muted-foreground" />
                            <span className="text-sm">{value}</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <ConditionalPhoneField
                                phoneNumber={customer.phone || ''}
                                customerId={customer.id}
                                className="text-sm"
                            />
                        </div>
                        {customer.additional_phone_number && (
                            <div className="flex items-center gap-1">
                                <ConditionalPhoneField
                                    phoneNumber={customer.additional_phone_number}
                                    customerId={customer.id}
                                    className="text-sm text-muted-foreground"
                                />
                            </div>
                        )}
                    </div>
                ),
            },
            {
                id: 'status',
                label: 'Status',
                key: 'status',
                sortable: true,
                width: '120px',
                render: (_value, customer) => (
                    <Badge
                        className={getStatusColor(customer.status?.name || '')}
                        style={{ backgroundColor: customer.status?.color }}
                    >
                        {customer.status?.label || '-'}
                    </Badge>
                ),
            },
            {
                id: 'kyc_status',
                label: 'KYC',
                key: 'kyc_status',
                sortable: true,
                width: '100px',
                render: (_value, customer) => (
                    <Badge
                        variant={customer.is_verified ? 'default' : 'secondary'}
                        className="text-xs"
                    >
                        {customer.kyc_status || 'pending'}
                    </Badge>
                ),
            },
            {
                id: 'country',
                label: 'Country',
                key: 'country',
                sortable: true,
                width: '120px',
                render: value => {
                    const country = value ? getCountryByCode(value) : null;
                    return value ? (
                        <div className="flex items-center gap-1">
                            <MapPin className="h-3 w-3 text-muted-foreground" />
                            <span className="text-sm">{country?.name || value}</span>
                        </div>
                    ) : (
                        <span className="text-muted-foreground">-</span>
                    );
                },
            },
            {
                id: 'trading_accounts',
                label: 'Accounts',
                key: 'trading_accounts',
                sortable: false,
                width: '100px',
                render: (_value, customer) => (
                    <div className="text-center">
                        <span className="font-medium">
                            {customer.trading_accounts?.length || 0}
                        </span>
                    </div>
                ),
            },
            {
                id: 'trading_stats',
                label: 'Trading',
                key: 'trading_stats',
                sortable: false,
                width: '120px',
                render: (_value, customer) => {
                    const stats = customer.trading_stats;
                    if (!stats) return <span className="text-muted-foreground">-</span>;

                    return (
                        <div className="text-xs space-y-1">
                            <div className="flex justify-between">
                                <span>Trades:</span>
                                <span className="font-medium">{stats.total_trades}</span>
                            </div>
                            <div className="flex justify-between">
                                <span>P&L:</span>
                                <span
                                    className={`font-medium ${stats.total_pnl >= 0 ? 'text-green-600' : 'text-red-600'}`}
                                >
                                    {stats.total_pnl >= 0 ? '+' : ''}
                                    {stats.total_pnl.toFixed(2)}
                                </span>
                            </div>
                        </div>
                    );
                },
            },
            {
                id: 'trading_permissions',
                label: 'Permissions',
                key: 'trading_permissions',
                sortable: false,
                width: '120px',
                render: (_value, customer) => {
                    const permissions = customer.trading_permissions;
                    if (!permissions) return <span className="text-muted-foreground">-</span>;

                    return (
                        <div className="text-xs space-y-1">
                            <div className="flex justify-between">
                                <span>Can Trade:</span>
                                <Badge
                                    variant={permissions.can_trade ? 'default' : 'secondary'}
                                    className="text-xs"
                                >
                                    {permissions.can_trade ? 'Yes' : 'No'}
                                </Badge>
                            </div>
                            <div className="flex justify-between">
                                <span>Type:</span>
                                <span className="font-medium capitalize">
                                    {permissions.is_professional
                                        ? 'Pro'
                                        : permissions.is_institutional
                                          ? 'Inst'
                                          : 'Standard'}
                                </span>
                            </div>
                        </div>
                    );
                },
            },
            {
                id: 'role',
                label: 'Role',
                key: 'role',
                sortable: true,
                width: '140px',
                render: (_value, customer) => (
                    <div className="flex items-center gap-1">
                        <Shield className="h-3 w-3 text-muted-foreground" />
                        <span className="text-sm capitalize">
                            {customer.role?.name || 'customer'}
                        </span>
                    </div>
                ),
            },
            {
                id: 'addresses',
                label: 'Addresses',
                key: 'addresses',
                sortable: false,
                width: '100px',
                render: (_value, customer) => (
                    <div className="text-center">
                        <span className="font-medium">{customer.addresses?.length || 0}</span>
                    </div>
                ),
            },
            {
                id: 'documents',
                label: 'Documents',
                key: 'documents',
                sortable: false,
                width: '120px',
                render: (_value, customer) => (
                    <div className="text-center">
                        <span className="font-medium">{customer.documents?.length || 0}</span>
                    </div>
                ),
            },
            {
                id: 'notes',
                label: 'Notes',
                key: 'notes',
                sortable: false,
                width: '100px',
                render: (_value, customer) => (
                    <div className="text-center">
                        <span className="font-medium">{customer.notes?.length || 0}</span>
                    </div>
                ),
            },
            {
                id: 'tasks',
                label: 'Tasks',
                key: 'tasks',
                sortable: false,
                width: '80px',
                render: (_value, customer) => (
                    <div className="text-center">
                        <span className="font-medium">{customer.tasks?.length || 0}</span>
                    </div>
                ),
            },
            {
                id: 'activities',
                label: 'Activities',
                key: 'activities',
                sortable: false,
                width: '100px',
                render: (_value, customer) => (
                    <div className="text-center">
                        <span className="font-medium">{customer.activities?.length || 0}</span>
                    </div>
                ),
            },
            {
                id: 'last_login_ip',
                label: 'Last IP',
                key: 'last_login_ip',
                sortable: true,
                width: '120px',
                render: value =>
                    value ? (
                        <div className="flex items-center gap-1">
                            <MapPin className="h-3 w-3 text-muted-foreground" />
                            <span className="text-sm">{value}</span>
                        </div>
                    ) : (
                        <span className="text-muted-foreground">-</span>
                    ),
            },
            {
                id: 'last_login',
                label: 'Last Login',
                key: 'last_login_at',
                sortable: true,
                width: '140px',
                render: value =>
                    value ? (
                        <DateTimeFormatter
                            date={value}
                            timeZone={crmTimezone}
                            showIcon={true}
                            icon={<Clock className="h-3 w-3 text-muted-foreground" />}
                            className="text-sm"
                        />
                    ) : (
                        <span className="text-muted-foreground">Never</span>
                    ),
            },
            {
                id: 'assigned_user',
                label: 'Assigned To',
                key: 'assigned_user_id',
                sortable: true,
                width: '120px',
                render: (_value, customer) =>
                    customer.assigned_user ? (
                        <div className="flex items-center gap-1">
                            <UserCheck className="h-3 w-3 text-muted-foreground" />
                            <span className="text-sm">{customer.assigned_user.name}</span>
                        </div>
                    ) : (
                        <span className="text-muted-foreground">Unassigned</span>
                    ),
            },
            {
                id: 'contact_attempts',
                label: 'Contact Attempts',
                key: 'contact_attempts',
                sortable: true,
                width: '140px',
                render: (_value, customer) => (
                    <div className="flex items-center gap-1">
                        <Activity className="h-3 w-3 text-muted-foreground" />
                        <span className="font-medium">{customer.contact_attempts || 0}</span>
                    </div>
                ),
            },
            {
                id: 'last_contact_date',
                label: 'Last Contact',
                key: 'last_contact_date',
                sortable: true,
                width: '140px',
                render: value =>
                    value ? (
                        <DateTimeFormatter
                            date={value}
                            timeZone={crmTimezone}
                            showIcon={true}
                            icon={<Clock className="h-3 w-3 text-muted-foreground" />}
                            className="text-sm"
                        />
                    ) : (
                        <span className="text-muted-foreground">Never</span>
                    ),
            },
            {
                id: 'next_follow_up_date',
                label: 'Next Follow-up',
                key: 'next_follow_up_date',
                sortable: true,
                width: '140px',
                render: value =>
                    value ? (
                        <DateTimeFormatter
                            date={value}
                            timeZone={crmTimezone}
                            showIcon={true}
                            icon={<Calendar className="h-3 w-3 text-muted-foreground" />}
                            className="text-sm"
                        />
                    ) : (
                        <span className="text-muted-foreground">None</span>
                    ),
            },
            {
                id: 'created_at',
                label: 'Created',
                key: 'created_at',
                sortable: true,
                width: '180px',
                render: value => (
                    <DateTimeFormatter date={value} timeZone={crmTimezone} className="text-sm" />
                ),
            },
            {
                id: 'converted_at',
                label: 'Conversion Date',
                key: 'converted_at',
                sortable: true,
                width: '160px',
                render: value =>
                    value ? (
                        <DateTimeFormatter
                            date={value}
                            timeZone={crmTimezone}
                            showIcon={true}
                            icon={<Target className="h-3 w-3 text-muted-foreground" />}
                            className="text-sm"
                        />
                    ) : (
                        <span className="text-muted-foreground">-</span>
                    ),
            },
            {
                id: 'updated_at',
                label: 'Updated',
                key: 'updated_at',
                sortable: true,
                width: '120px',
                render: value => (
                    <DateTimeFormatter date={value} timeZone={crmTimezone} className="text-sm" />
                ),
            },
            // Add custom attribute columns dynamically
            ...customAttributeDefinitions.map(definition => ({
                id: `custom_${definition.name}`,
                label: definition.label,
                key: `custom_attributes.${definition.name}` as any,
                sortable: true, // Enable sorting for custom attributes
                width: '150px',
                render: (_: any, customer: Customer) => {
                    const customValue = customer.custom_attributes?.[definition.name as any]?.value;
                    if (!customValue) {
                        return <span className="text-muted-foreground">-</span>;
                    }

                    // Format the value based on field type
                    switch (definition.field_type) {
                        case 'boolean':
                            return (
                                <Badge variant={customValue ? 'default' : 'secondary'}>
                                    {customValue ? 'Yes' : 'No'}
                                </Badge>
                            );
                        case 'date':
                            return (
                                <DateTimeFormatter
                                    date={customValue}
                                    timeZone={crmTimezone}
                                    className="text-sm"
                                />
                            );
                        case 'multiselect':
                            return (
                                <div className="text-sm">
                                    {Array.isArray(customValue)
                                        ? customValue.join(', ')
                                        : customValue}
                                </div>
                            );
                        default:
                            return <span className="text-sm">{String(customValue)}</span>;
                    }
                },
            })),
        ],
        [customAttributeDefinitions, crmTimezone]
    );

    const defaultVisibleCustomerColumns = useMemo(
        () => columns.map(col => col.id).filter(id => id !== 'customer_number'),
        [columns]
    );

    // Column preferences with localStorage persistence
    const { visibleColumns, handleColumnToggle, handleResetColumns, setVisibleColumnsFromView } =
        useColumnPreferences({
            storageKey: 'customers-page-visible-columns',
            defaultColumns: defaultVisibleCustomerColumns,
            columns,
            introducedColumns: ['country'],
        });

    const handleClearFilters = () => {
        clearFilters();
        handleResetColumns();
    };

    /** Drives the empty state: "no matches" reads differently from "nothing yet". */
    const hasActiveFilters = useMemo(
        () => Object.values(filters ?? {}).some(v => v !== '' && v !== null && v !== undefined),
        [filters]
    );

    // Views management
    const {
        views,
        isLoading: isLoadingViews,
        selectedViewId,
        handleViewSelect,
        handleSaveAsView,
        handleUpdateView,
        handleDeleteView,
        handleReorderViews,
        isSavingView,
        isAdmin: isAdminUser,
    } = usePageViews({
        module: 'customers',
        currentFilters: filters,
        currentSort: sort,
        currentVisibleColumns: visibleColumns,
        defaultColumns: columns.map(col => col.id),
        currentPageSize: pagination.pageSize,
        onApplyViewState: ({
            filters: nextFilters,
            sort: nextSort,
            pagination: nextPagination,
        }) => {
            replaceState({
                filters: nextFilters,
                sort: nextSort ?? undefined,
                pagination: nextPagination,
            });
        },
        onFiltersChange: updateFilters,
        onSortChange: ({ key, direction }) => {
            if (sort.key !== key) {
                toggleSort(key);
                if (direction === 'desc') {
                    setTimeout(() => toggleSort(key), 0);
                }
            } else if (sort.direction !== direction) {
                toggleSort(key);
            }
        },
        onVisibleColumnsChange: cols => {
            setVisibleColumnsFromView(cols);
        },
    });

    // Helper functions for styling
    function getStatusColor(status: any) {
        // Handle new LeadStatus object format
        if (typeof status === 'object' && status !== null) {
            // Use the color from the LeadStatus object
            return `text-white border-transparent`;
        }

        // Handle old string format (fallback)
        if (typeof status === 'string') {
            switch (status.toLowerCase()) {
                case 'active':
                    return 'bg-green-100 text-green-800 border-green-300';
                case 'inactive':
                    return 'bg-yellow-100 text-yellow-800 border-yellow-300';
                case 'suspended':
                    return 'bg-red-100 text-red-800 border-red-300';
                default:
                    return 'bg-gray-100 text-gray-800 border-gray-300';
            }
        }

        return 'bg-gray-100 text-gray-800 border-gray-300';
    }

    const { hasPermission } = usePermissions();

    // Define filter fields for the GenericDataTable
    const filterFields = useMemo(
        () => [
            {
                key: 'search',
                label: 'Search',
                type: 'text' as const,
                placeholder: 'Search customers...',
            },
            {
                key: 'status',
                label: 'Status',
                type: 'select' as const,
                options: [
                    ...leadStatuses.map(status => ({
                        value: status.id,
                        label: status.label,
                    })),
                ],
            },
            {
                key: 'country',
                label: 'Country',
                type: 'select' as const,
                options: [
                    ...countries.map(country => ({ value: country.name, label: country.name })),
                ],
            },
            { key: 'city', label: 'City', type: 'text' as const, placeholder: 'Enter city...' },
            { key: 'state', label: 'State', type: 'text' as const, placeholder: 'Enter state...' },
            {
                key: 'document_status',
                label: 'Document Status',
                type: 'select' as const,
                options: [
                    { value: 'pending', label: 'Pending' },
                    { value: 'approved', label: 'Approved' },
                    { value: 'rejected', label: 'Rejected' },
                ],
            },
            {
                key: 'document_type',
                label: 'Document Type',
                type: 'select' as const,
                options: [
                    { value: 'id_proof', label: 'ID Proof' },
                    { value: 'address_proof', label: 'Address Proof' },
                    { value: 'other', label: 'Other' },
                ],
            },
            {
                key: 'assigned_user_id',
                label: 'Assigned To',
                type: 'select' as const,
                options: [
                    { value: 'unassigned', label: 'Unassigned' },
                    ...users.map(user => ({ value: user.id, label: user.name })),
                ],
            },
            { key: 'created_before', label: 'Created Before', type: 'date' as const },
            { key: 'last_login_after', label: 'Last Login After', type: 'date' as const },
            { key: 'last_login_before', label: 'Last Login Before', type: 'date' as const },
            {
                key: 'has_phone',
                label: 'Has Phone',
                type: 'boolean' as const,
                options: [
                    { value: 'true', label: 'Yes' },
                    { value: 'false', label: 'No' },
                ],
            },
            {
                key: 'has_documents',
                label: 'Has Documents',
                type: 'boolean' as const,
                options: [
                    { value: 'true', label: 'Yes' },
                    { value: 'false', label: 'No' },
                ],
            },
            {
                key: 'has_addresses',
                label: 'Has Addresses',
                type: 'boolean' as const,
                options: [
                    { value: 'true', label: 'Yes' },
                    { value: 'false', label: 'No' },
                ],
            },
            {
                key: 'contact_attempts_min',
                label: 'Min Contact Attempts',
                type: 'number' as const,
                placeholder: 'Minimum attempts...',
            },
            {
                key: 'contact_attempts_max',
                label: 'Max Contact Attempts',
                type: 'number' as const,
                placeholder: 'Maximum attempts...',
            },
            { key: 'last_contact_after', label: 'Last Contact After', type: 'date' as const },
            { key: 'last_contact_before', label: 'Last Contact Before', type: 'date' as const },
            { key: 'next_follow_up_after', label: 'Next Follow-up After', type: 'date' as const },
            { key: 'next_follow_up_before', label: 'Next Follow-up Before', type: 'date' as const },
            {
                key: 'has_contact',
                label: 'Has Contact',
                type: 'boolean' as const,
                options: [
                    { value: 'true', label: 'Yes' },
                    { value: 'false', label: 'No' },
                ],
            },
            {
                key: 'has_follow_up',
                label: 'Has Follow-up',
                type: 'boolean' as const,
                options: [
                    { value: 'true', label: 'Yes' },
                    { value: 'false', label: 'No' },
                ],
            },
            // Add custom attribute filters dynamically
            ...(customAttributeDefinitions || []).map(definition => ({
                key: `custom_attributes.${definition.name}`,
                label: definition.label,
                type:
                    definition.field_type === 'boolean'
                        ? ('boolean' as const)
                        : definition.field_type === 'date'
                          ? ('date' as const)
                          : definition.field_type === 'number'
                            ? ('number' as const)
                            : definition.field_type === 'select' ||
                                definition.field_type === 'multiselect'
                              ? ('select' as const)
                              : ('text' as const),
                placeholder:
                    definition.field_type === 'text'
                        ? `Search ${definition.label.toLowerCase()}...`
                        : undefined,
                options:
                    definition.field_type === 'select' || definition.field_type === 'multiselect'
                        ? definition.options?.map(option => ({ value: option, label: option }))
                        : undefined,
            })),
        ],
        [customAttributeDefinitions, users, leadStatuses, crmTimezone]
    );

    const handleFilterChange = (newFilters: FilterConfig) => {
        updateFilters(newFilters);
    };

    // Column management handlers are now provided by useColumnPreferences hook

    // Page size change handler
    const handlePageSizeChange = (newPageSize: number) => {
        updatePagination({ page: 1, pageSize: newPageSize });
    };

    // Bulk actions handlers
    const bulkUpdateStatusMutation = useBulkUpdateCustomerStatus();
    const bulkAssignMutation = useBulkAssignCustomers();
    const bulkDeleteMutation = useBulkDeleteCustomers();

    const handleBulkStatusChange = () => {
        if (selectedCustomers.length === 0) return;
        setBulkStatusDialogOpen(true);
    };

    const handleBulkStatusUpdate = () => {
        if (!selectedStatusId) return;
        bulkUpdateStatusMutation.mutate(
            { customerIds: selectedCustomers, statusId: selectedStatusId },
            {
                onSuccess: () => {
                    enqueueSnackbar(
                        `Successfully updated status for ${selectedCustomers.length} customer${selectedCustomers.length !== 1 ? 's' : ''}`,
                        { variant: 'success', autoHideDuration: 3000 }
                    );
                    setSelectedCustomers([]);
                    setBulkStatusDialogOpen(false);
                    setSelectedStatusId('');
                },
                onError: (error: any) => {
                    console.error('Bulk status update error:', error);
                    const errors = error.response?.data?.errors;
                    const errorMessage =
                        errors?.status_id?.[0] ||
                        errors?.customer_ids?.[0] ||
                        error.response?.data?.message ||
                        error.response?.data?.error ||
                        error.message ||
                        'Failed to update customer statuses';
                    enqueueSnackbar(errorMessage, {
                        variant: 'error',
                        autoHideDuration: 5000,
                    });
                },
            }
        );
    };

    const handleBulkAssign = () => {
        if (selectedCustomers.length === 0) return;
        setBulkAssignDialogOpen(true);
    };

    const handleBulkAssignUpdate = () => {
        if (selectedUserId === '' || selectedUserId === undefined) return;
        const isUnassign = selectedUserId === '__unassigned__';
        bulkAssignMutation.mutate(
            {
                customerIds: selectedCustomers,
                assignedUserId: isUnassign ? null : selectedUserId,
            },
            {
                onSuccess: () => {
                    setSelectedCustomers([]);
                    setBulkAssignDialogOpen(false);
                    setSelectedUserId('');
                },
            }
        );
    };

    const handleBulkDelete = () => {
        if (selectedCustomers.length === 0) return;

        const confirmed = window.confirm(
            `Are you sure you want to delete ${selectedCustomers.length} customer${selectedCustomers.length !== 1 ? 's' : ''}? This action cannot be undone.`
        );

        if (confirmed) {
            bulkDeleteMutation.mutate(selectedCustomers, {
                onSuccess: () => {
                    setSelectedCustomers([]);
                },
            });
        }
    };

    const { data: customers, isLoading } = useQuery<PaginatedResponse<Customer>>({
        queryKey: ['customers', pagination.page, pagination.pageSize, filters, sort],
        queryFn: async () => {
            const { data } = await httpClient.get<PaginatedResponse<Customer>>(
                API_ENDPOINTS.CRM.CUSTOMERS.LIST,
                {
                    params: buildApiParams({
                        page: pagination.page,
                        per_page: pagination.pageSize,
                        ...viewParams,
                    }),
                }
            );
            return data;
        },
    });

    const createCustomerMutation = useCreateCustomer();

    const handleAddCustomer = () => {
        setCreateDialogOpen(true);
    };

    const handleCreateCustomer = (data: any) => {
        createCustomerMutation.mutate(data, {
            onSuccess: () => {
                enqueueSnackbar('Customer created successfully', { variant: 'success' });
                setCreateDialogOpen(false);
            },
            onError: (error: any) => {
                if (error.response?.data?.errors) {
                    const errorMessages = Object.values(error.response.data.errors).flat();
                    errorMessages.forEach((message: any) => {
                        enqueueSnackbar(String(message), { variant: 'error' });
                    });
                } else {
                    enqueueSnackbar('Failed to create customer', { variant: 'error' });
                }
            },
        });
    };

    // Statistics
    const stats = useMemo(() => {
        const activeCustomers =
            customers?.data.filter((customer: Customer) => customer.status?.name === 'active')
                .length || 0;
        const inactiveCustomers =
            customers?.data.filter((customer: Customer) => customer.status?.name === 'inactive')
                .length || 0;
        const suspendedCustomers =
            customers?.data.filter((customer: Customer) => customer.status?.name === 'suspended')
                .length || 0;

        const recentCustomers =
            customers?.data.filter((customer: Customer) => {
                const createdAt = new Date(customer.created_at);
                const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
                return createdAt >= thirtyDaysAgo;
            }).length || 0;

        return {
            total: customers?.meta?.total || 0,
            active: activeCustomers,
            inactive: inactiveCustomers,
            suspended: suspendedCustomers,
            recent: recentCustomers,
        };
    }, [customers]);

    return (
        <PageContainer fullHeight>
            <PageHeader
                title="Customers"
                description="Manage and track your customers"
                actions={
                    hasPermission(ModulePermission.CUSTOMERS, PermissionAction.CREATE) ? (
                        <Button onClick={handleAddCustomer}>
                            <Plus className="h-4 w-4" />
                            Add Customer
                        </Button>
                    ) : undefined
                }
            />

            <StatTileGrid className="shrink-0 xl:grid-cols-5">
                <StatTile
                    label="Total Customers"
                    value={stats.total}
                    icon={Users}
                    tone="brand"
                    isLoading={isLoading}
                />
                <StatTile
                    label="Active"
                    value={stats.active}
                    icon={UserCheck}
                    tone="success"
                    isLoading={isLoading}
                />
                <StatTile
                    label="Inactive"
                    value={stats.inactive}
                    icon={UserX}
                    tone="warning"
                    isLoading={isLoading}
                />
                <StatTile
                    label="Suspended"
                    value={stats.suspended}
                    icon={UserX}
                    tone="destructive"
                    isLoading={isLoading}
                />
                <StatTile
                    label="Recent (30d)"
                    value={stats.recent}
                    icon={Calendar}
                    tone="info"
                    isLoading={isLoading}
                />
            </StatTileGrid>

            {/* Bulk Actions Toolbar */}
            {selectedCustomers.length > 0 && (
                <div className="flex shrink-0 items-center justify-between rounded-lg border border-primary/30 bg-primary/5 p-3">
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-foreground">
                            {selectedCustomers.length} customer
                            {selectedCustomers.length !== 1 ? 's' : ''} selected
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        {hasPermission(ModulePermission.CUSTOMERS, PermissionAction.EDIT) && (
                            <Button variant="outline" size="sm" onClick={handleBulkStatusChange}>
                                <Edit className="h-4 w-4" />
                                Change Status
                            </Button>
                        )}
                        {hasPermission(ModulePermission.CUSTOMERS, PermissionAction.EDIT) && (
                            <Button variant="outline" size="sm" onClick={handleBulkAssign}>
                                <UserPlus className="h-4 w-4" />
                                Change Assignee
                            </Button>
                        )}
                        {hasPermission(ModulePermission.CUSTOMERS, PermissionAction.EXPORT) && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleExportCustomers(selectedCustomers)}
                                disabled={exportCustomersMutation.isPending}
                            >
                                <Download className="h-4 w-4" />
                                {exportCustomersMutation.isPending
                                    ? 'Exporting…'
                                    : 'Export Selected'}
                            </Button>
                        )}
                        {hasPermission(ModulePermission.CUSTOMERS, PermissionAction.DELETE) && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={handleBulkDelete}
                                disabled={bulkDeleteMutation.isPending}
                                className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                            >
                                <Trash2 className="h-4 w-4" />
                                {bulkDeleteMutation.isPending ? 'Deleting…' : 'Delete'}
                            </Button>
                        )}
                    </div>
                </div>
            )}

            {/* Generic Data Table */}
            {/* View Selector */}
            <ViewSelector
                views={views}
                selectedViewId={selectedViewId}
                onViewSelect={handleViewSelect}
                onSaveAsView={handleSaveAsView}
                onUpdateView={handleUpdateView}
                onDeleteView={handleDeleteView}
                onReorderViews={handleReorderViews}
                isSavingView={isSavingView}
                isAdmin={isAdminUser}
                isLoading={isLoadingViews}
                filterFields={filterFields}
                columns={columns}
            />

            <GenericDataTable<Customer>
                data={customers?.data || []}
                columns={columns}
                visibleColumns={visibleColumns}
                filters={filters}
                filterFields={filterFields}
                presets={[]}
                isLoading={isLoading || isLoadingCustomAttributeDefinitions}
                selectedItems={selectedCustomers}
                onSelectionChange={setSelectedCustomers}
                getItemId={customer => customer.id}
                sortConfig={{
                    key: sort.key as keyof Customer,
                    direction: sort.direction,
                }}
                onSort={handleSort}
                totalItems={customers?.meta?.total || 0}
                totalPages={
                    customers?.meta ? Math.ceil(customers.meta.total / pagination.pageSize) : 1
                }
                currentPage={pagination.page}
                pageSize={pagination.pageSize}
                onPageChange={goToPage}
                onPageSizeChange={handlePageSizeChange}
                onExport={() => handleExportCustomers()}
                isExporting={exportCustomersMutation.isPending}
                showExportButton={hasPermission(
                    ModulePermission.CUSTOMERS,
                    PermissionAction.EXPORT
                )}
                onRowClick={customer => {
                    // Build query string from current filters and sort
                    const queryParams = new URLSearchParams();

                    // Add filters (remove filter_ prefix)
                    searchParams.forEach((value, key) => {
                        if (key.startsWith('filter_')) {
                            const filterKey = key.replace('filter_', '');
                            queryParams.set(filterKey, value);
                        }
                    });

                    // Add sort params (transform to sort_key and sort_direction); use same default as list when none in URL
                    const sortKey = searchParams.get('sort') || 'created_at';
                    const sortDirection = searchParams.get('direction') || 'desc';
                    queryParams.set('sort_key', sortKey);
                    queryParams.set('sort_direction', sortDirection);

                    const queryString = queryParams.toString();
                    navigate(`/customers/${customer.id}${queryString ? `?${queryString}` : ''}`);
                }}
                onFilterChange={handleFilterChange}
                onClearFilters={handleClearFilters}
                onColumnToggle={handleColumnToggle}
                onResetColumns={handleResetColumns}
                emptyMessage="No customers found"
                loadingMessage="Loading customers…"
                stickyLayout
                emptyState={
                    <EmptyState
                        icon={Users}
                        title="No customers found"
                        description={
                            hasActiveFilters
                                ? 'No customer matches the current filters. Try clearing them.'
                                : 'Customers will appear here once they register or you add them.'
                        }
                        action={
                            hasActiveFilters ? (
                                <Button variant="outline" size="sm" onClick={handleClearFilters}>
                                    Clear filters
                                </Button>
                            ) : hasPermission(
                                  ModulePermission.CUSTOMERS,
                                  PermissionAction.CREATE
                              ) ? (
                                <Button size="sm" onClick={handleAddCustomer}>
                                    <Plus className="h-4 w-4" />
                                    Add Customer
                                </Button>
                            ) : undefined
                        }
                    />
                }
            />

            {/* Create Customer Dialog */}
            <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Create New Customer</DialogTitle>
                    </DialogHeader>
                    <CustomerForm
                        onSubmit={handleCreateCustomer}
                        onCancel={() => setCreateDialogOpen(false)}
                        isLoading={createCustomerMutation.isPending}
                        mode="create"
                    />
                </DialogContent>
            </Dialog>

            {/* Bulk Status Change Dialog */}
            <Dialog open={bulkStatusDialogOpen} onOpenChange={setBulkStatusDialogOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>
                            Change Status for {selectedCustomers.length} Customer
                            {selectedCustomers.length !== 1 ? 's' : ''}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <label className="text-sm font-medium">Select New Status</label>
                            <Select value={selectedStatusId} onValueChange={setSelectedStatusId}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Choose a status..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {leadStatuses.map(status => (
                                        <SelectItem key={status.id} value={status.id}>
                                            <div className="flex items-center gap-2">
                                                <div
                                                    className="w-3 h-3 rounded-full"
                                                    style={{ backgroundColor: status.color }}
                                                />
                                                {status.label}
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setBulkStatusDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleBulkStatusUpdate}
                            disabled={!selectedStatusId || bulkUpdateStatusMutation.isPending}
                        >
                            {bulkUpdateStatusMutation.isPending ? 'Updating...' : 'Update Status'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Bulk Assign Dialog */}
            <Dialog open={bulkAssignDialogOpen} onOpenChange={setBulkAssignDialogOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>
                            Assign {selectedCustomers.length} Customer
                            {selectedCustomers.length !== 1 ? 's' : ''}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <label className="text-sm font-medium">Select User</label>
                            <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Choose a user..." />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="__unassigned__">
                                        <div className="flex items-center gap-2">
                                            <UserCheck className="h-4 w-4" />
                                            Unassigned
                                        </div>
                                    </SelectItem>
                                    {users.map(user => (
                                        <SelectItem key={user.id} value={user.id}>
                                            <div className="flex items-center gap-2">
                                                <UserCheck className="h-4 w-4" />
                                                {user.name}
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setBulkAssignDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleBulkAssignUpdate}
                            disabled={
                                selectedUserId === '' ||
                                selectedUserId === undefined ||
                                bulkAssignMutation.isPending
                            }
                        >
                            {bulkAssignMutation.isPending
                                ? selectedUserId === '__unassigned__'
                                    ? 'Unassigning...'
                                    : 'Assigning...'
                                : selectedUserId === '__unassigned__'
                                  ? 'Unassign'
                                  : 'Assign'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </PageContainer>
    );
};

export default CustomersPage;
